import React, { createContext, useContext, useState, useEffect } from 'react';

const LearnerAuthContext = createContext(null);

export const LearnerAuthProvider = ({ children }) => {
  const [learner, setLearner] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('learner_token'));
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api/learn`;

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setLearner(data);
          } else {
            localStorage.removeItem('learner_token');
            setToken(null);
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          localStorage.removeItem('learner_token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [token, API]);

  const login = async (email, password) => {
    const response = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    localStorage.setItem('learner_token', data.token);
    setToken(data.token);
    setLearner(data.learner);
    return data;
  };

  const register = async (userData) => {
    const response = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    localStorage.setItem('learner_token', data.token);
    setToken(data.token);
    setLearner(data.learner);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('learner_token');
    setToken(null);
    setLearner(null);
  };

  const refreshLearner = async () => {
    if (token) {
      try {
        const response = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setLearner(data);
        }
      } catch (error) {
        console.error('Failed to refresh learner:', error);
      }
    }
  };

  return (
    <LearnerAuthContext.Provider value={{
      learner,
      token,
      loading,
      isAuthenticated: !!token && !!learner,
      login,
      register,
      logout,
      refreshLearner
    }}>
      {children}
    </LearnerAuthContext.Provider>
  );
};

export const useLearnerAuth = () => {
  const context = useContext(LearnerAuthContext);
  if (!context) {
    throw new Error('useLearnerAuth must be used within a LearnerAuthProvider');
  }
  return context;
};
